/**
 * The authenticated frame, and the sidebar that groups the product by pillar.
 *
 * **The sidebar is grouped because the product has pillars, not because a flat
 * list looked untidy.** Since slice 65 there are four: Reach (paid ads on the
 * workspace's own Meta ad account), Connect (email and WhatsApp campaigns),
 * Sense (five screens since slice 62: Today, Funnel, Pressure, Outcomes, Action
 * ledger) and Flow (journeys, built since slice 69). Flat, that would be
 * sixteen items and more as each pillar grows, and by then everyone who uses this
 * has muscle memory pointing at the flat list. Grouping costs nothing now and a
 * relearn later, so it happened early, in slice 15, before anybody had used it.
 *
 * Slices 0 through 5 grew this navigation one disabled placeholder at a time,
 * and the `link()` helper that shape needed has gone with it: **the structure
 * is no longer written here.** `navigationTree()` in `@wend/shared` says which
 * sections sit under which pillar, which sit in the shared and workspace
 * groups, and what may be said about a pillar; this file supplies the things a
 * shared package has no business holding — the words, the icons and the paths.
 * {@link NAV_DESTINATIONS} is a `Record<NavSectionId, …>` on purpose: a
 * section added to the contract with nowhere to go fails to compile here
 * rather than rendering a gap. A pillar's own name and glyph come from
 * `lib/pillars.tsx`, the one place the Overview's strip and the page headers
 * read them from too.
 *
 * **The order, top to bottom: Overview (no heading), Reach, Connect, Sense,
 * Flow, "Audiences & templates", Workspace.** The pillars run in `PILLAR_ORDER`,
 * which ends in Flow — get people, talk to the ones you hold, learn what
 * worked, act on it per person. Audiences,
 * Segments and Templates sat under the old Reach until slice 65 split it; they
 * belong to no pillar now (Connect sends to them, Sense reads them), so they
 * are their own group below the pillars rather than filed under one of them.
 *
 * **Who sees which pillar group — slice 65's rule, built on slices 26 and 59.**
 *
 *   - A pillar that is not built is absent: no heading, no note, no route
 *     behind a "coming soon" page — a URL that gets shared, bookmarked and
 *     indexed and then has to keep working through the slice that actually
 *     builds it (slice 26). That was Flow's rule until slice 69 built it; Flow
 *     now shows like Reach and Sense — on the plan, its Flows list; not on the
 *     plan, absent — and its one address, `/flows`, was born with the screen.
 *   - **Connect always shows.** Entitled, it holds Campaigns; not entitled, it
 *     says "Not on this plan" and holds no links. It is the pillar that
 *     explains an empty Overview — the Overview's dashboard is Connect's
 *     content — which is the job the Reach group had before the split, and a
 *     workspace staring at an empty Overview with no sidebar line saying why is
 *     the support ticket this rule prevents.
 *   - **Reach and Sense show only when they are on the plan.** An unentitled
 *     one is absent, not a heading with a note: it would explain nothing on
 *     this workspace's screens and read as an upsell (slice 59's rule for
 *     Sense, now Reach's too, since an ads-less workspace's screens say nothing
 *     about ads).
 *
 * `navigationTree` still returns a verdict for every pillar; `buildNav` decides
 * which verdicts earn a group, and that split is deliberate — the contract
 * states what is true, this file decides what is worth saying.
 *
 * `navPillarSchema` makes the emptiness structural — only an `available`
 * pillar has a `sections` field at all — so a link into software that does not
 * exist, or into a pillar that is not on the plan, is not constructible here or
 * in a later edit, filter or no filter.
 *
 * **The pillar verdict is read, never recomputed.** `navigationTree` derives it
 * through `overviewPillar`, so this sidebar and the Overview's pillar cards are
 * one verdict rendered twice rather than two verdicts that can disagree, and
 * `not_built` keeps beating `not_entitled` in one place. That precedence is
 * slice 10's and it stands: being sold something that does not exist is worse
 * than being told it is not on your plan, and "not built yet" stays true after
 * they buy it.
 *
 * "Not on this plan" is a statement about a plan, not a lock. The routes still
 * resolve and the API still answers — entitlement is not enforced on those
 * endpoints, and slice 65 recorded why it still is not (a guard would refuse
 * live sends on any workspace whose rows lagged a deploy). It matches what the
 * Overview says with the same verdict; a sidebar link to a screen the Overview
 * has just said is not on this plan is exactly the two-answers problem the
 * shared pillar vocabulary exists to stop.
 *
 * **Analytics and Reports stay under Workspace, and Workspace is not a pillar.**
 * That argument is slice 13's and 14's and slice 65 did not reopen it — if
 * anything the split made it stronger, because both screens compare surfaces
 * across Reach and Connect. Analytics answers "what went out between these two
 * dates, and how many distinct people did it reach" — a question about a
 * *period*, asked after the fact, not a step in getting a message out of the
 * door; Reports is the same history asked as a question a spreadsheet can
 * answer, and the two are genuinely alternatives, so they sit together with
 * Analytics above because it is the cheaper of the two and the one somebody
 * wants first. Integrations and Credits sit with them for the reason that makes
 * Workspace a group rather than a pillar: they serve whatever pillars a tenant
 * has, they do not become a copy per pillar, and nobody is ever "not entitled"
 * to their own credit ledger. Integrations keeps all three tabs, the Meta ad
 * account's included, for the same reason.
 *
 * Group headings carry the pillar's glyph through `AppShell`'s `icon`, drawn
 * beside the `h2` and never inside it, so each heading's text — which a screen
 * reader hears and verify-nav-shell reads — is the word alone. Workspace has no
 * glyph: it is the machinery, and a quieter heading is right for it.
 *
 * `user` is nullable on the context but cannot be null here — `AppLayout` only
 * renders under `RequireAuth`, which holds a spinner until the session
 * resolves. The fallback is every feature rather than none, because inventing a
 * plan restriction out of a session the app has not finished reading would hide
 * working screens from someone who is entitled to them; Flow is still
 * `not_built` under it, since that verdict never consults entitlement.
 *
 * The active item is computed from the current location rather than hard-coded.
 * With several real destinations a fixed flag would highlight the wrong one
 * and, worse, put `aria-current="page"` on it.
 */
import {
  FEATURE_VALUES,
  navigationTree,
  type Feature,
  type NavPillar,
  type NavSectionId,
  type PillarState,
} from '@wend/shared';
import { AppShell, Button, type AppShellNavItem, type AppShellNavSection } from '@wend/ui';
import {
  BarChart3,
  ChartBarDecreasing,
  Columns3,
  Coins,
  Contact,
  FileSpreadsheet,
  FileText,
  Gauge,
  History,
  LayoutDashboard,
  ListChecks,
  ListFilter,
  Megaphone,
  Plug,
  Send,
  Target,
  UserPlus,
  Workflow,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { PILLAR_IDENTITY, PILLAR_LABELS, SHARED_GROUP, isBuiltPillar, pillarIsShown } from '../lib/pillars';

/* -------------------------------------------------------------------------- */
/* The words, the icons and the paths                                          */
/* -------------------------------------------------------------------------- */

interface NavDestination {
  readonly label: string;
  readonly icon: ReactNode;
  readonly path: string;
  /**
   * Whether being *here* means this exact path, or this path and anything under
   * it. Both the shell's highlight and `NavLink`'s own `aria-current` are
   * driven from this one flag so they cannot disagree: `/` matches every path
   * unless the match ends there, while `/audiences` has to stay the current
   * section while a dataset or the upload screen is open.
   */
  readonly exact: boolean;
}

/**
 * Every section the contract names, with somewhere to go.
 *
 * Typed as a total `Record` over `NavSectionId` deliberately — see the header.
 * The ids are the shared vocabulary; the labels are what a tenant recognises,
 * and the two are allowed to differ.
 */
const NAV_DESTINATIONS: Readonly<Record<NavSectionId, NavDestination>> = {
  overview: { label: 'Overview', icon: <LayoutDashboard />, path: '/', exact: true },
  audiences: { label: 'Audiences', icon: <Contact />, path: '/audiences', exact: false },
  segments: { label: 'Segments', icon: <ListFilter />, path: '/segments', exact: false },
  templates: { label: 'Templates', icon: <FileText />, path: '/templates', exact: false },
  campaigns: { label: 'Campaigns', icon: <Send />, path: '/campaigns', exact: false },
  integrations: { label: 'Integrations', icon: <Plug />, path: '/integrations', exact: false },
  credits: { label: 'Credits', icon: <Coins />, path: '/credits', exact: false },
  analytics: { label: 'Analytics', icon: <BarChart3 />, path: '/analytics', exact: false },
  reports: { label: 'Reports', icon: <FileSpreadsheet />, path: '/reports', exact: false },
  // Sense: Today and the ledger (slice 62), funnel and outcomes (60), pressure (59).
  today: { label: 'Today', icon: <ListChecks />, path: '/sense/today', exact: false },
  funnel: { label: 'Funnel', icon: <ChartBarDecreasing />, path: '/sense/funnel', exact: false },
  pressure: { label: 'Pressure', icon: <Gauge />, path: '/sense/pressure', exact: false },
  outcomes: { label: 'Outcomes', icon: <Target />, path: '/sense/outcomes', exact: false },
  actions: { label: 'Action ledger', icon: <History />, path: '/sense/actions', exact: false },
  // Reach (slice 65): Wend's ad campaigns on the workspace's Meta ad account.
  // "Meta ads" is the ads Wend drafted; "Meta ad account" is the whole account
  // and lives under Integrations. Not exact: a draft and the composer sit under
  // it and keep it highlighted.
  ads: { label: 'Meta ads', icon: <Megaphone />, path: '/reach/ads', exact: false },
  // Slices 67–70: Reach's leads, the field catalogue beside the uploads it is
  // computed from, and the Flow pillar's list of flows (the builder opens from it).
  leads: { label: 'Leads', icon: <UserPlus />, path: '/reach/leads', exact: false },
  fields: { label: 'Fields', icon: <Columns3 />, path: '/audiences/fields', exact: false },
  flows: { label: 'Flows', icon: <Workflow />, path: '/flows', exact: false },
};

/**
 * What a group with no destinations says, in as few words as it can be said.
 *
 * Word for word what the Overview's `PillarSection` badge says, because two screens
 * describing the same state in two phrasings is how a reader learns they are
 * two different states. `Exclude<PillarState, 'available'>` rather than a
 * loose map: a fourth pillar state stops compiling here, which is the only
 * moment anybody would think about what the sidebar should say about it.
 */
const PILLAR_NOTES: Readonly<Record<Exclude<PillarState, 'available'>, string>> = {
  not_built: 'Not built yet',
  not_entitled: 'Not on this plan',
};

/* -------------------------------------------------------------------------- */
/* Building the sidebar                                                        */
/* -------------------------------------------------------------------------- */

function under(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function destination(id: NavSectionId, pathname: string): AppShellNavItem {
  const { label, icon, path, exact } = NAV_DESTINATIONS[id];
  /*
   * Slice 67: a destination can live under another's path — Fields is
   * `/audiences/fields`, under Audiences' `/audiences`. The deeper one wins, so
   * exactly one item is current there, and `aria-current` is withdrawn from
   * the shallower link explicitly (NavLink alone would mark both).
   */
  const deeper = Object.values(NAV_DESTINATIONS).some(
    (other) => other.path !== path && other.path.startsWith(`${path}/`) && under(pathname, other.path),
  );
  const active = !deeper && (exact ? pathname === path : under(pathname, path));

  return {
    key: id,
    label,
    icon,
    active,
    render: ({ className, children, onClick }) => (
      <NavLink to={path} end={exact} className={className} onClick={onClick} {...(deeper ? { 'aria-current': false } : {})}>
        {children}
      </NavLink>
    ),
  };
}

/**
 * One pillar group.
 *
 * The unbuilt arms carry no `items`, and the shell's section union refuses to
 * hold both a note and a destination — so "Not built yet" above a link is not a
 * thing this file can render by mistake.
 *
 * The label and glyph come from `lib/pillars.tsx`. `PILLAR_LABELS` is total
 * over `Feature` so this compiles for any pillar the tree returns; only a built
 * one has a glyph, and `buildNav` only passes built ones in.
 */
function pillarSection(pillar: NavPillar, pathname: string): AppShellNavSection {
  const label = PILLAR_LABELS[pillar.feature];
  const icon = isBuiltPillar(pillar.feature) ? PILLAR_IDENTITY[pillar.feature].icon : undefined;

  if (pillar.state !== 'available') {
    return { key: pillar.feature, label, icon, note: PILLAR_NOTES[pillar.state] };
  }

  return {
    key: pillar.feature,
    label,
    icon,
    items: pillar.sections.map((id) => destination(id, pathname)),
  };
}

/**
 * Whether a pillar's group is drawn at all — slice 65. See the file header for
 * the whole rule; in one line: built, and then either Connect (always, so an
 * empty Overview is explained) or on the plan. The rule is `pillarIsShown` in
 * `lib/pillars.tsx`, which the Overview's strip asks too, so the two screens
 * cannot drift into showing a workspace different pillars.
 */
function pillarGroupShows(pillar: NavPillar): boolean {
  return pillarIsShown(pillar.feature, pillar.state);
}

function buildNav(pathname: string, features: readonly Feature[]): AppShellNavSection[] {
  const tree = navigationTree(features);

  return [
    // No heading: a heading over a single "Overview" link says the word twice.
    { key: 'home', items: [destination(tree.home, pathname)] },
    /*
     * THE PILLARS — Reach, Connect, Sense, Flow, in `PILLAR_ORDER` (Flow
     * since slice 69, through the identity in `lib/pillars.tsx` alone).
     *
     * Filtered on whether the software exists first (slice 26), and only then
     * on the plan. The two filters look interchangeable and are not:
     * `state !== 'available'` alone would also drop the Connect group for a
     * workspace that is not entitled to it, and that group is *supposed* to
     * render — as "Not on this plan", with no links — because it is what
     * explains an empty Overview. Before slice 65 that was the Reach group's
     * job, when Reach meant campaigns; the campaigns are Connect's now, so the
     * job moved with them. An unentitled Reach or Sense is simply absent, as an
     * unbuilt pillar is (slice 59): on this workspace's screens neither would
     * explain anything, and a heading-plus-note for them reads as an upsell.
     *
     * The contract is untouched by either filter: `navigationTree` still
     * derives every verdict, so the day Flow ships the first filter passes it
     * through with no other edit here than its identity in `lib/pillars.tsx`.
     */
    ...tree.pillars.filter(pillarGroupShows).map((pillar) => pillarSection(pillar, pathname)),
    /*
     * THE SHARED LAYERS — slice 65. Audiences, Segments, Templates: what every
     * pillar works on, so under none of them, and never "not on this plan".
     */
    {
      key: 'shared',
      label: SHARED_GROUP.label,
      icon: SHARED_GROUP.icon,
      items: tree.shared.map((id) => destination(id, pathname)),
    },
    {
      key: 'workspace',
      label: 'Workspace',
      // Pinned to the foot of the sidebar, above the signed-in user: the
      // product scrolls, the workspace's own screens stay put (AppShell header).
      pinned: true,
      items: tree.workspace.map((id) => destination(id, pathname)),
    },
  ];
}

export interface AppLayoutProps {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

export function AppLayout({ title, actions, children }: AppLayoutProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const nav = buildNav(pathname, user?.features ?? FEATURE_VALUES);

  async function onSignOut(): Promise<void> {
    if (signingOut) return;
    setSigningOut(true);
    await signOut();
    // The guard would redirect on its own, but saying so explicitly keeps the
    // history entry for the app out of the back button.
    navigate('/login', { replace: true });
  }

  return (
    <AppShell
      title={title}
      actions={actions}
      nav={nav}
      sidebarFooter={
        <div className="flex flex-col gap-2.5">
          {user !== null && (
            <div className="px-1">
              <p className="truncate text-[0.85rem] font-medium text-text">{user.name}</p>
              <p className="truncate font-mono text-[0.66rem] tracking-[0.06em] text-muted">
                {user.email}
              </p>
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            fullWidth
            loading={signingOut}
            onClick={() => {
              void onSignOut();
            }}
          >
            {signingOut ? 'Signing out' : 'Sign out'}
          </Button>
        </div>
      }
    >
      {children}
    </AppShell>
  );
}
